/**
 * ODBC API Client
 *
 * API client for ODBC driver listing and connection templates.
 */

import { httpClient } from "./client";
import {
  assertObject,
  assertString,
  assertBoolean,
  assertArrayOf,
  assertOptionalString,
} from "../types/validation";

// ============================================================================
// Type Definitions
// ============================================================================

export interface IOdbcDriverAttribute {
  readonly key: string;
  readonly value: string;
}

export interface IOdbcDriver {
  readonly name: string;
  readonly attributes: readonly IOdbcDriverAttribute[];
}

const ODBC_FIELD_TYPE_VALUES = ['text', 'number', 'password'] as const;
export type TOdbcFieldType = typeof ODBC_FIELD_TYPE_VALUES[number];

export interface IOdbcTemplateField {
  readonly key: string;
  readonly label: string;
  readonly placeholder: string;
  readonly required: boolean;
  readonly defaultValue?: string;
  readonly fieldType: TOdbcFieldType;
}

export interface IOdbcTemplate {
  readonly id: string;
  readonly name: string;
  readonly dialect: string;
  readonly fields: readonly IOdbcTemplateField[];
}

export interface IOdbcApiClient {
  listDrivers(): Promise<readonly IOdbcDriver[]>;
  listTemplates(): Promise<readonly IOdbcTemplate[]>;
}

// ============================================================================
// Validators
// ============================================================================

function validateOdbcDriverAttribute(data: unknown, path: string): IOdbcDriverAttribute {
  const obj = assertObject(data, path);
  return {
    key: assertString(obj['key'], `${path}.key`),
    value: assertString(obj['value'], `${path}.value`),
  };
}

export function validateOdbcDriver(data: unknown, path: string): IOdbcDriver {
  const obj = assertObject(data, path);
  return {
    name: assertString(obj['name'], `${path}.name`),
    attributes: assertArrayOf(obj['attributes'], validateOdbcDriverAttribute, `${path}.attributes`),
  };
}

function validateFieldType(value: unknown, path: string): TOdbcFieldType {
  const str = assertString(value, path);
  if (!ODBC_FIELD_TYPE_VALUES.includes(str as TOdbcFieldType)) {
    throw new Error(`Invalid field type at '${path}': expected one of ${ODBC_FIELD_TYPE_VALUES.join(', ')}, got '${str}'`);
  }
  return str as TOdbcFieldType;
}

export function validateOdbcTemplateField(data: unknown, path: string): IOdbcTemplateField {
  const obj = assertObject(data, path);
  return {
    key: assertString(obj['key'], `${path}.key`),
    label: assertString(obj['label'], `${path}.label`),
    placeholder: assertString(obj['placeholder'], `${path}.placeholder`),
    required: assertBoolean(obj['required'], `${path}.required`),
    defaultValue: assertOptionalString(obj['defaultValue'], `${path}.defaultValue`),
    fieldType: validateFieldType(obj['fieldType'], `${path}.fieldType`),
  };
}

export function validateOdbcTemplate(data: unknown, path: string): IOdbcTemplate {
  const obj = assertObject(data, path);
  return {
    // Идентификатор шаблона — слаг («postgresql», «sqlserver»), не число.
    id: assertString(obj['id'], `${path}.id`),
    name: assertString(obj['name'], `${path}.name`),
    dialect: assertString(obj['dialect'], `${path}.dialect`),
    fields: assertArrayOf(obj['fields'], validateOdbcTemplateField, `${path}.fields`),
  };
}

// ============================================================================
// API Client Implementation
// ============================================================================

export class OdbcApiClient implements IOdbcApiClient {
  async listDrivers(): Promise<readonly IOdbcDriver[]> {
    const response = await httpClient.get<unknown>("/odbc/drivers");
    return assertArrayOf(response.data, validateOdbcDriver, 'listDrivers');
  }

  async listTemplates(): Promise<readonly IOdbcTemplate[]> {
    const response = await httpClient.get<unknown>("/odbc/templates");
    return assertArrayOf(response.data, validateOdbcTemplate, 'listTemplates');
  }
}

// Singleton instance
let odbcApiClient: OdbcApiClient | null = null;

export function getOdbcApiClient(): OdbcApiClient {
  odbcApiClient ??= new OdbcApiClient();
  return odbcApiClient;
}
